import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import {
  INSTITUTION_OWNERSHIP_DEFAULTS,
  type InstitutionOwnership,
  type InstitutionOwnershipOrDefaults,
  institutionDetailsOwnershipApiToInstitutionOwnership,
  institutionOwnershipCorporateOwnersSchema,
  institutionOwnershipListingSchema,
  institutionOwnershipManagementSchema,
  institutionOwnershipStructureSchema,
  institutionOwnershipToInstitutionDetailsOwnershipApi,
  institutionOwnershipUbosSchema,
  isListedOwnership,
} from '@/features/institution/ownership/schema';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { reportTaxResidenceFormatErrors } from '@/features/tax-residency/report';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import type { FormStep } from '@/types/components';
import { InstitutionOwnershipFieldsetChart } from './fieldsets/chart';
import { InstitutionOwnershipFieldsetCorporateOwners, InstitutionOwnershipFieldsetCorporateOwnersLegend } from './fieldsets/corporate-owners';
import { InstitutionOwnershipFieldsetHasCorporateOwners } from './fieldsets/has-corporate-owners';
import { InstitutionOwnershipFieldsetHasOwners } from './fieldsets/has-owners';
import { InstitutionOwnershipFieldsetListing } from './fieldsets/listing';
import { InstitutionOwnershipFieldsetManagement, InstitutionOwnershipFieldsetManagementLegend } from './fieldsets/management';
import { InstitutionOwnershipFieldsetType } from './fieldsets/type';
import { InstitutionOwnershipFieldsetUbos, InstitutionOwnershipFieldsetUbosLegend } from './fieldsets/ubos';

const InstitutionOwnershipFormStepListingLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.ownership.listing.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.ownership.listing.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionOwnershipFormStepListingLegend.displayName = 'InstitutionOwnershipFormStepListingLegend';

const InstitutionOwnershipFormStepStructureLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.ownership.structure.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.ownership.structure.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionOwnershipFormStepStructureLegend.displayName = 'InstitutionOwnershipFormStepStructureLegend';

const InstitutionOwnershipFormStepStructure = () => (
  <div className="space-y-6">
    <InstitutionOwnershipFieldsetType />
    <InstitutionOwnershipFieldsetChart />
    <InstitutionOwnershipFieldsetHasCorporateOwners />
  </div>
);
InstitutionOwnershipFormStepStructure.displayName = 'InstitutionOwnershipFormStepStructure';

// A listed company and its wholly owned subsidiaries name no UBOs, so their route runs from the listing straight to management
const beforeStructureStepEnter: FormStepGuard<InstitutionOwnership> = async ({ direction, form, steps }, next) => {
  if (!form) return next(false);

  if (direction === 'next' && isListedOwnership(form.getValues('regulatedMarketListing'))) return next(steps.indexOf(InstitutionOwnershipFieldsetManagement));

  return next();
};

InstitutionOwnershipFormStepStructure.beforeStepEnter = beforeStructureStepEnter;

const InstitutionOwnershipFormStepUbos = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const hasOwners = form.watch('hasOwners');

  return (
    <div className="space-y-6">
      <InstitutionOwnershipFieldsetHasOwners />
      {hasOwners ? <InstitutionOwnershipFieldsetUbos /> : null}
    </div>
  );
};
InstitutionOwnershipFormStepUbos.displayName = 'InstitutionOwnershipFormStepUbos';

const beforeUbosStepLeave: FormStepGuard<InstitutionOwnership> = async ({ direction, form }, next) => {
  if (direction === 'next') await reportTaxResidenceFormatErrors(form.getValues('ubos').flatMap(({ taxResidences }) => taxResidences));

  return next();
};

InstitutionOwnershipFormStepUbos.beforeStepLeave = beforeUbosStepLeave;

const beforeUbosStepEnter: FormStepGuard<InstitutionOwnership> = async ({ direction, form, steps }, next) => {
  if (!form) return next(false);

  if (direction === 'prev' && isListedOwnership(form.getValues('regulatedMarketListing'))) return next(steps.indexOf(InstitutionOwnershipFieldsetListing));

  return next();
};

InstitutionOwnershipFormStepUbos.beforeStepEnter = beforeUbosStepEnter;

const FORM_STEPS = {
  LISTING: {
    Component: InstitutionOwnershipFieldsetListing,
    Legend: InstitutionOwnershipFormStepListingLegend,
    schema: institutionOwnershipListingSchema,
  },
  STRUCTURE: {
    Component: InstitutionOwnershipFormStepStructure,
    Legend: InstitutionOwnershipFormStepStructureLegend,
    schema: institutionOwnershipStructureSchema,
  },
  CORPORATE_OWNERS: {
    Component: InstitutionOwnershipFieldsetCorporateOwners,
    Legend: InstitutionOwnershipFieldsetCorporateOwnersLegend,
    schema: institutionOwnershipCorporateOwnersSchema,
  },
  UBOS: {
    Component: InstitutionOwnershipFormStepUbos,
    Legend: InstitutionOwnershipFieldsetUbosLegend,
    schema: institutionOwnershipUbosSchema,
  },
  MANAGEMENT: {
    Component: InstitutionOwnershipFieldsetManagement,
    Legend: InstitutionOwnershipFieldsetManagementLegend,
    schema: institutionOwnershipManagementSchema,
  },
} satisfies Record<string, FormStep<InstitutionOwnership>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionOwnershipFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionOwnership>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'ownership', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionOwnershipFormCurrentStep.displayName = 'InstitutionOwnershipFormCurrentStep';

const InstitutionOwnershipFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionOwnership>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionOwnershipFormCurrentStepLegend.displayName = 'InstitutionOwnershipFormCurrentStepLegend';

const InstitutionOwnershipFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionOwnership>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/business-profile`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionOwnershipFormBackButton.displayName = 'InstitutionOwnershipFormBackButton';

const InstitutionOwnershipFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionOwnership>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionOwnershipFormFooter.displayName = 'InstitutionOwnershipFormFooter';

const InstitutionOwnershipForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'ownership');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionOwnership, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    await mutateAsync(
      { ownership: await institutionOwnershipToInstitutionDetailsOwnershipApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/regulatory-information`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.ownership
    ? institutionDetailsOwnershipApiToInstitutionOwnership(institutionDetailsAnswers.ownership)
    : INSTITUTION_OWNERSHIP_DEFAULTS;

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionOwnershipFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionOwnershipFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionOwnership) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionOwnershipFormCurrentStep />
          <InstitutionOwnershipFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionOwnershipForm.displayName = 'InstitutionOwnershipForm';

export default InstitutionOwnershipForm;
